import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
    Shield,
    Plus,
    Search,
    Edit,
    Trash2,
    Eye,
    CheckCircle,
    XCircle
} from 'lucide-react'
import api from '../services/api'
import RoleModal from '../components/RoleModal'

const Roles = () => {
    const { hasPermission } = useAuth()
    const [roles, setRoles] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [selectedRole, setSelectedRole] = useState(null)
    const [showRoleModal, setShowRoleModal] = useState(false)
    const [editingRole, setEditingRole] = useState(null)

    useEffect(() => {
        fetchRoles()
    }, [currentPage, searchTerm])

    const fetchRoles = async () => {
        try {
            const params = new URLSearchParams({
                page: currentPage,
                limit: 10,
                ...(searchTerm && { search: searchTerm })
            })

            const response = await api.get(`/roles?${params}`)
            setRoles(response.data.data.roles)
            setTotalPages(response.data.data.pagination.pages)
        } catch (error) {
            // Error fetching roles - handle gracefully
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e) => {
        e.preventDefault()
        setCurrentPage(1)
        fetchRoles()
    }

    const toggleRoleStatus = async (roleId) => {
        try {
            await api.patch(`/roles/${roleId}/toggle-status`)
            fetchRoles()
        } catch (error) {
            console.error('Error toggling role status:', error)
        }
    }

    const deleteRole = async (roleId) => {
        if (!window.confirm('Are you sure you want to delete this role?')) return

        try {
            await api.delete(`/roles/${roleId}`)
            fetchRoles()
        } catch (error) {
            console.error('Error deleting role:', error)
        }
    }

    const handleAddRole = () => {
        setEditingRole(null)
        setShowRoleModal(true)
    }

    const handleEditRole = (role) => {
        setEditingRole(role)
        setShowRoleModal(true)
    }

    const handleRoleSuccess = () => {
        fetchRoles()
    }

    const getPermissionIcon = (hasPermission) => {
        return hasPermission ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
            <XCircle className="h-4 w-4 text-gray-400" />
        )
    }

    if (!hasPermission('roles', 'read')) {
        return (
            <div className="text-center py-12">
                <Shield className="h-12 w-12 text-indigo-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-600">You don't have permission to view roles.</p>
            </div>
        )
    }

    return (
        <div className="space-y-8 max-w-7xl mx-auto px-2 md:px-6 py-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-indigo-700 tracking-tight">Roles</h1>
                    <p className="text-gray-600">Manage system roles and permissions</p>
                </div>
                {hasPermission('roles', 'create') && (
                    <button
                        onClick={handleAddRole}
                        className="p-1.5 btn flex items-center gap-2 rounded-lg shadow-md bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white transition"
                    >
                        <Plus className="h-4 w-4" />
                        Add Role
                    </button>
                )}
            </div>

            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow p-4">
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search roles..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input pl-10 w-full focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                            />
                        </div>
                    </div>
                    <button type="submit" className="btn py-2 rounded-lg shadow bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white transition cust_padding">
                        Search
                    </button>
                </form>
            </div>

            {/* Roles Table */}
            <div className="bg-white rounded-xl shadow p-4 overflow-x-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-indigo-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                                    Role
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                                    Level
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                                    Permissions (Cameras & Users)
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-blue-700 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {roles.map((role) => (
                                <tr key={role._id} className="hover:bg-blue-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-semibold text-gray-900">
                                                {role.name}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {role.description}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                                            Level {role.level}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${role.isActive
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                            }`}>
                                            {role.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex space-x-4">
                                            {/* Camera Permissions */}
                                            <div className="flex flex-col space-y-1">
                                                <div className="text-xs font-medium text-gray-700">Cameras</div>
                                                <div className="flex space-x-1">
                                                    <span title="Read" className="cursor-help">{getPermissionIcon(role.permissions?.cameras?.read)}</span>
                                                    <span title="Create" className="cursor-help">{getPermissionIcon(role.permissions?.cameras?.create)}</span>
                                                    <span title="Update" className="cursor-help">{getPermissionIcon(role.permissions?.cameras?.update)}</span>
                                                    <span title="Delete" className="cursor-help">{getPermissionIcon(role.permissions?.cameras?.delete)}</span>
                                                    <span title="Playback" className="cursor-help">{getPermissionIcon(role.permissions?.cameras?.playback)}</span>
                                                </div>
                                            </div>
                                            {/* User Permissions */}
                                            <div className="flex flex-col space-y-1">
                                                <div className="text-xs font-medium text-gray-700">Users</div>
                                                <div className="flex space-x-1">
                                                    <span title="Read" className="cursor-help">{getPermissionIcon(role.permissions?.users?.read)}</span>
                                                    <span title="Create" className="cursor-help">{getPermissionIcon(role.permissions?.users?.create)}</span>
                                                    <span title="Update" className="cursor-help">{getPermissionIcon(role.permissions?.users?.update)}</span>
                                                    <span title="Delete" className="cursor-help">{getPermissionIcon(role.permissions?.users?.delete)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end space-x-2">
                                            {hasPermission('roles', 'read') && (
                                                <button
                                                    onClick={() => setSelectedRole(role)}
                                                    className="text-cyan-600 hover:text-cyan-900"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                            )}
                                            {hasPermission('roles', 'update') && (
                                                <button
                                                    onClick={() => handleEditRole(role)}
                                                    className="text-cyan-500 hover:text-cyan-700"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                            )}
                                            {hasPermission('roles', 'delete') && (
                                                <button
                                                    onClick={() => deleteRole(role._id)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Permissions Legend */}
            <div className="bg-white rounded-xl shadow p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Permissions Legend</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-medium text-gray-700 mb-2">Camera Permissions</h4>
                        <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Read - View camera information and live streams</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Create - Add new cameras to the system</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Update - Modify camera settings and properties</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Delete - Remove cameras from the system</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Playback - Access recorded footage and playback functionality</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-medium text-gray-700 mb-2">User Permissions</h4>
                        <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Read - View user information</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Create - Add new users to the system</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Update - Modify user information and settings</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span>Delete - Remove users from the system</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                    <div className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="btn btn-outline"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="btn btn-outline"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Role Modal */}
            <RoleModal
                isOpen={showRoleModal}
                onClose={() => {
                    setShowRoleModal(false)
                    setEditingRole(null)
                }}
                role={editingRole}
                onSuccess={handleRoleSuccess}
            />
        </div>
    )
}

export default Roles 